import { inject } from '@angular/core';
import { FirestoreError } from '@angular/fire/firestore';
import { CanMatchFn } from '@angular/router';
import { Logger } from '@core/logger';
import { CollectionPaths } from '@models/collections';
import { Matter } from '@models/matter';
import { Store } from '@ngxs/store';
import {
	FIRESTORE_ERROR_CODES,
	FirestoreRepo,
} from '@repos/firestore-repo.service';
import { NavigationService } from '@services/nav/nav.service';
import { UserProfileState } from '@state/user-profile';
import { FirebaseError } from 'firebase/app';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

export const MatterRestrictedGuard: CanMatchFn = (_, matterIdSegment) => {
	const matterId = matterIdSegment[1].path; // used on the matter route only /matter/:matterId -> ["/matter", "{{matterId}}"]
	const navigationService = inject(NavigationService);
	const store = inject(Store);
	const firestore = new FirestoreRepo<Matter, { businessId: string }>(
		({ businessId }) => CollectionPaths.business_matters(businessId),
	);
	if (!matterId) {
		navigationService.goTo404();
		return false;
	}

	return store.select(UserProfileState.getCurrentBusinessId).pipe(
		filter((businessId) => !!businessId),
		switchMap((businessId) => firestore.Get(matterId, { businessId })),
		map((matterDoc) => matterDoc.exists()),
		tap((matterExists) => !matterExists && navigationService.goTo404()),
		catchError((error) => {
			Logger.exception(error);
			if (error instanceof FirebaseError || error instanceof FirestoreError) {
				switch (error.code) {
					case FIRESTORE_ERROR_CODES.PERMISSION_DENIED:
						navigationService.goTo403();
						break;
					default:
						navigationService.goToDashboard();
						break;
				}
			} else {
				navigationService.goToDashboard();
			}
			return of(false);
		}),
	);
};
